import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";
import { type AppTheme, VALID_THEMES, migrateTheme } from "@/lib/themes";
import { normalizeBackendUrl } from "@/lib/api";

export type { AppTheme };
const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL as string | undefined);
const TOKEN_KEY = "token";

interface ThemeContextValue {
  theme: AppTheme;
  setTheme: (t: AppTheme, options?: { persistRemote?: boolean }) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "relaxed-grey",
  setTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<AppTheme>(() => {
    try {
      const stored = localStorage.getItem("cpl_theme");
      return migrateTheme(stored);
    } catch (err) {
      console.warn("[theme] Failed to read stored theme:", err);
      return "deep-navy";
    }
  });

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    try {
      localStorage.setItem("cpl_theme", theme);
    } catch (err) {
      console.warn("[theme] Failed to persist theme preference:", err);
    }
  }, [theme]);

  const setTheme = useCallback((t: AppTheme, options?: { persistRemote?: boolean }) => {
    if (!(VALID_THEMES as string[]).includes(t)) return;
    setThemeState(t);
    if (options?.persistRemote === false) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    fetch(`${BACKEND_URL}/api/user/preferences`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ appTheme: t }),
    }).catch(() => {});
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
